import { useEffect } from "react";
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { auth } from "./firebase";
import { track } from "./analytics";

// Google Identity Services types (minimal — la librería se carga vía CDN en index.html).
type CredentialResponse = { credential: string; select_by?: string };
type IdConfig = {
  client_id: string;
  callback: (resp: CredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  use_fedcm_for_prompt?: boolean;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: IdConfig) => void;
          prompt: () => void;
          cancel: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

function waitForGis(maxMs = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.google?.accounts?.id) return resolve(true);
    const start = Date.now();
    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start > maxMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });
}

const DISMISSED_KEY = "fideliza:onetap_dismissed_until";
const DISMISS_HOURS = 12;

function isDismissed(): boolean {
  try {
    const until = parseInt(localStorage.getItem(DISMISSED_KEY) ?? "0", 10);
    return Date.now() < until;
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(
      DISMISSED_KEY,
      String(Date.now() + DISMISS_HOURS * 3600 * 1000)
    );
  } catch {
    /* ignore */
  }
}

/**
 * Hook que muestra el widget Google One-Tap si:
 *  - el usuario no está autenticado todavía,
 *  - el client_id está configurado,
 *  - GIS cargó,
 *  - el usuario no lo dismisseó recientemente.
 *
 * Es no-op gracioso si algo falla.
 */
export function useGoogleOneTap(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    if (isDismissed()) return;
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    let cancelled = false;

    (async () => {
      const ok = await waitForGis();
      if (cancelled || !ok || !window.google) return;

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            track("onetap_credential_received");
            try {
              const credential = GoogleAuthProvider.credential(response.credential);
              await signInWithCredential(auth, credential);
              track("onetap_signed_in");
            } catch (err) {
              track("onetap_sign_in_failed", {
                code: (err as { code?: string })?.code ?? "unknown"
              });
            }
          },
          auto_select: false,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: true
        });
        window.google.accounts.id.prompt();
        track("onetap_prompted");
      } catch (err) {
        track("onetap_init_failed", {
          code: (err as { code?: string })?.code ?? "unknown"
        });
      }
    })();

    return () => {
      cancelled = true;
      try {
        window.google?.accounts.id.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [enabled]);
}

/** Llamar en logout para resetear el estado de One-Tap. */
export function resetOneTap(): void {
  try {
    window.google?.accounts.id.disableAutoSelect();
  } catch {
    /* ignore */
  }
}

export { markDismissed as markOneTapDismissed };
